import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { setIsTldAlertModalOpen } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrJobTypes, type TableDataRow } from 'src/utils/jmrUtils';
import { toggleJob } from 'src/utils/placementSelectionUtils';
import { filterTableRows } from 'src/utils/tableUtils';

import TldAlertModal from '../../tldAlertModal/tldAlertModal';

import ExpandableMultiShiftJobRow from './expandableMultiShiftJobRow';
import SingleShiftJobRow from './singleJobRow';
import T2PlusModificationsModal from './t2PlusModificationsModal';

import ApplyModificationsModal from './applyModificationModal/applyModificationsModal';

interface JobMatchesTableProps {
  searchField: string;
  jobsExceedingRestrictionsInAggregate: Set<string>;
  exceededRestrictionIds: Set<number>;
  /** For scrolling job row into view */
  selectedJobRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  stickyHeight: number;
}

const JobMatchesTable = ({
  searchField,
  jobsExceedingRestrictionsInAggregate,
  exceededRestrictionIds,
  selectedJobRefs,
  stickyHeight,
}: JobMatchesTableProps) => {
  const { jmrFilter, jmrResults, tldAlertModal, tldAlertModalAcknowledged } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const dispatch = useAppDispatch();
  const [jobToModify, setJobToModify] = useState<null | TableDataRow>(null);
  const [shiftToModify, setShiftToModify] = useState<string | null>(null);
  const idToFocus = useRef<string | null>(null);
  const isT2Experience = useIsT2Experience();
  const focusSelection = (resultId: string) => {
    idToFocus.current = resultId;
  };
  /**
   * useLayoutEffect fires synchronously after the DOM has been updated, guaranteeing that
   * selectedJobRefs is populated before we attempt to focus. No dependency array is intentional —
   * the null guard makes this a no-op on most renders, and omitting the array avoids using
   * an unrelated value as a misleading timing mechanism.
   */
  useLayoutEffect(() => {
    if (idToFocus.current === null) return;
    if (!selectedJobRefs.current.has(idToFocus.current)) return;
    selectedJobRefs.current.get(idToFocus.current)?.focus();
    idToFocus.current = null;
  });

  const filteredTableData = useMemo(
    () =>
      filterTableRows(jmrResults, searchField, [
        'shiftId',
        'jobId',
        'totalHours',
        'perfectMatch',
        'isChecked',
        'rankingScore',
      ]).filter((row) => row.jobType === jmrFilter.jobType),
    [jmrResults, jmrFilter.jobType, searchField],
  );

  const onClickModify = (row: TableDataRow, shiftId?: string) => {
    if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModalAcknowledged) {
      dispatch(
        setIsTldAlertModalOpen({
          open: true,
          resultId: row.id,
        }),
      );
    } else {
      setJobToModify(row);
      setShiftToModify(shiftId ?? null);
    }
  };
  const onConfirmTldModal = () => {
    const job = filteredTableData.find((row) => row.id === tldAlertModal.resultId);
    if (!job) {
      return;
    }
    if (job.conflicts?.length && !job.maximumHoursAssociateCanDo) {
      setJobToModify(job);
    } else {
      toggleJob(tldAlertModal.resultId, jmrResults, dispatch);
      focusSelection(tldAlertModal.resultId);
    }
  };

  return (
    <>
      <Table
        layout="auto"
        fixHeaderRows
        fixHeaderRowsOffset={stickyHeight}
        headerRows={1}
        showStripes={true}
        spacing="small"
        nestedRows
        rowComponents={[UncontrolledExpandableRow, TableRow, ExpandableMultiShiftJobRow, SingleShiftJobRow]}
      >
        <TableRow>
          <TableCell sortColumn="rank">Job</TableCell>
          <TableCell sortColumn="name">Shift</TableCell>
          <TableCell>Conflicts</TableCell>
          <TableCell>Action</TableCell>
          <TableCell></TableCell>
          <TableCell width={80}></TableCell>
        </TableRow>
        {filteredTableData.map((row, index) =>
          row.shifts.length > 1 ? (
            <ExpandableMultiShiftJobRow
              key={index}
              row={row}
              selectedJobRefs={selectedJobRefs}
              jobsExceedingRestrictionsInAggregate={jobsExceedingRestrictionsInAggregate}
              onClickModify={onClickModify}
              exceededRestrictionIds={exceededRestrictionIds}
            />
          ) : (
            <SingleShiftJobRow
              key={index}
              row={row}
              selectedJobRefs={selectedJobRefs}
              jobsExceedingRestrictionsInAggregate={jobsExceedingRestrictionsInAggregate}
              exceededRestrictionIds={exceededRestrictionIds}
              onClickModify={onClickModify}
              focusSelection={focusSelection}
            />
          ),
        )}
      </Table>

      <TldAlertModal isOpen={tldAlertModal.open} onConfirm={() => onConfirmTldModal()} />
      {isT2Experience ? (
        <T2PlusModificationsModal
          jobToModify={jobToModify}
          shiftToModify={shiftToModify}
          isOpen={!!jobToModify}
          onClose={() => setJobToModify(null)}
        />
      ) : (
        <ApplyModificationsModal
          jobToModify={jobToModify}
          shiftToModify={shiftToModify}
          exceededRestrictionIds={exceededRestrictionIds}
          isOpen={!!jobToModify}
          focusSelection={focusSelection}
          onClose={() => setJobToModify(null)}
        />
      )}
    </>
  );
};
export default JobMatchesTable;
